// src/components/SubmitTime/hooks/useTimeCalculations.ts
// Custom hook for time calculations and parsing
// Migrated from SubmitTime.js time-related methods

import { useCallback } from 'react';

export interface TimeCalculations {
  parseTime: (timeString: string) => string;
  parseRemaining: (timeString: string) => string;
  sumTimeToFloat: (time1: string, time2: number) => string;
  sumTimeToFloatUpTo24: (time1: string, time2: number) => string;
  subTimeToFloat: (time1: string, time2: number) => string;
  splitTimes: (totalMinutes: number) => { hours: string; minutes: string }[];
  timeToMinutes: (timeString: string) => number;
  minutesToTime: (minutes: number) => string;
  padTo2Digits: (num: number) => string;
  formatDate: (date: Date) => string;
  formatStrDateToMMDDYYY: (dateStr: string) => string;
  isValidDate: (d: any) => boolean;
}

export function useTimeCalculations(
  _hoursAdjust: number = 0,
  _minutesAdjust: number = 0,
): TimeCalculations
{
  // Helper to pad numbers to 2 digits
  const padTo2Digits = useCallback((num: number): string =>
  {
    return num < 10 ? '0' + num : String(num);
  }, []);

  // Parse time input on blur — matches legacy parseTime() exactly.
  // Accepts: "5" → "5:00", "5.5" → "5:30", "5:35" → "5:30" (rounds to nearest 10 min)
  // Returns '' if the input contains letters (caller should show hoursFormat error).
  const parseTime = useCallback((timeString: string): string =>
  {
    if (!timeString) return '';

    const timeStr = timeString.toString().trim();

    // Contains letters → invalid format
    if (/[a-zA-Z]/.test(timeStr)) return '';

    let hour: string;
    let minutesStr: string; // keep as string for substring rounding

    if (timeStr.includes('.'))
    {
      // Decimal format: "5.5", "5.50" — legacy uses parseInt(minutePart * 0.60 / 0.99)
      const pointPos = timeStr.indexOf('.');
      hour = timeStr.substring(0, pointPos);
      const afterDot = timeStr.substring(pointPos + 1);
      const minutesInt = parseInt(String((parseInt(afterDot) * 0.6) / 0.99));
      minutesStr = String(isNaN(minutesInt) ? 0 : minutesInt);
    }
    else if (timeStr.includes(':'))
    {
      // Colon format: "5:35"
      const colonPos = timeStr.indexOf(':');
      hour = timeStr.substring(0, colonPos);
      minutesStr = timeStr.substring(colonPos + 1);
    }
    else
    {
      // Plain number: "5"
      hour = timeStr;
      minutesStr = '00';
    }

    // Only minutes are capped at 2 chars; hours can be any length (e.g. 100h sprints)
    if (minutesStr.length > 2) return '';

    // Round minutes to nearest 10: take first character of the minutes string + "0"
    // e.g. "35" → "30", "08" → "00", "5" → "50"
    const roundedMinutes = minutesStr.substring(0, 1) + '0';

    return `${hour || '00'}:${roundedMinutes}`;
  }, []);

  // Parse remaining time with special handling
  const parseRemaining = useCallback(
    (timeString: string): string =>
    {
      return parseTime(timeString);
    },
    [parseTime],
  );

  // Convert time string (HH:MM) to total minutes
  const timeToMinutes = useCallback((timeString: string): number =>
  {
    if (!timeString) return 0;

    const parts = timeString.split(':');
    const hours = parseInt(parts[0] ?? '0') || 0;
    const minutes = parseInt(parts[1] ?? '0') || 0;
    return hours * 60 + minutes;
  }, []);

  // Convert minutes to time string (HH:MM)
  const minutesToTime = useCallback(
    (totalMinutes: number): string =>
    {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
    },
    [padTo2Digits],
  );

  // Sum time string and float hours, return as time string
  const sumTimeToFloat = useCallback(
    (time1: string, time2: number): string =>
    {
      const minutes1 = timeToMinutes(time1);
      const minutes2 = Math.round(time2 * 60);
      const totalMinutes = minutes1 + minutes2;
      return minutesToTime(totalMinutes);
    },
    [timeToMinutes, minutesToTime],
  );

  // Sum time with special minute rounding (legacy conversion logic)
  const sumTimeToFloatUpTo24 = useCallback(
    (time1: string, time2: number): string =>
    {
      const minutes1 = timeToMinutes(time1);

      // Legacy minute conversion logic
      // 1-10 min → 0.17 (10%), 20-29 → 0.33 (30%), 30-39 → 0.5 (50%), etc.
      let convertedTime2 = time2;
      if (time2 % 1 !== 0)
      {
        const decimal = time2 % 1;
        const hours = Math.floor(time2);

        let minuteValue = 0;
        if (decimal >= 0.17 && decimal < 0.33) minuteValue = 10;
        else if (decimal >= 0.33 && decimal < 0.5) minuteValue = 20;
        else if (decimal >= 0.5 && decimal < 0.67) minuteValue = 30;
        else if (decimal >= 0.67 && decimal < 0.83) minuteValue = 40;
        else if (decimal >= 0.83) minuteValue = 50;

        convertedTime2 = hours + minuteValue / 60;
      }

      const minutes2 = Math.round(convertedTime2 * 60);
      const totalMinutes = Math.min(minutes1 + minutes2, 24 * 60); // Max 24 hours
      return minutesToTime(totalMinutes);
    },
    [timeToMinutes, minutesToTime],
  );

  // Subtract float hours from time string
  const subTimeToFloat = useCallback(
    (time1: string, time2: number): string =>
    {
      const minutes1 = timeToMinutes(time1);
      const minutes2 = Math.round(time2 * 60);
      const totalMinutes = Math.max(0, minutes1 - minutes2);
      return minutesToTime(totalMinutes);
    },
    [timeToMinutes, minutesToTime],
  );

  // Split total minutes into multiple 24-hour chunks if needed
  const splitTimes = useCallback(
    (totalMinutes: number): { hours: string; minutes: string }[] =>
    {
      const result: { hours: string; minutes: string }[] = [];
      const maxMinutesPerDay = 24 * 60; // 1440 minutes

      let remainingMinutes = totalMinutes;
      while (remainingMinutes > 0)
      {
        const minutesForThisDay = Math.min(remainingMinutes, maxMinutesPerDay);
        const hours = Math.floor(minutesForThisDay / 60);
        const minutes = minutesForThisDay % 60;

        result.push({
          hours: padTo2Digits(hours),
          minutes: padTo2Digits(minutes),
        });

        remainingMinutes -= minutesForThisDay;
      }

      return result;
    },
    [padTo2Digits],
  );

  // Format date to YYYY-MM-DD
  const formatDate = useCallback(
    (date: Date): string =>
    {
      const year = date.getFullYear();
      const month = padTo2Digits(date.getMonth() + 1);
      const day = padTo2Digits(date.getDate());
      return `${year}-${month}-${day}`;
    },
    [padTo2Digits],
  );

  // Convert date string to MM/DD/YYYY format
  const formatStrDateToMMDDYYY = useCallback(
    (dateStr: string): string =>
    {
      if (!dateStr) return '';

      const date = new Date(dateStr);
      if (!isValidDate(date)) return dateStr;

      const month = padTo2Digits(date.getMonth() + 1);
      const day = padTo2Digits(date.getDate());
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    [padTo2Digits],
  );

  // Validate if a value is a valid date
  const isValidDate = useCallback((d: any): boolean =>
  {
    return d instanceof Date && !isNaN(d.getTime());
  }, []);

  return {
    parseTime,
    parseRemaining,
    sumTimeToFloat,
    sumTimeToFloatUpTo24,
    subTimeToFloat,
    splitTimes,
    timeToMinutes,
    minutesToTime,
    padTo2Digits,
    formatDate,
    formatStrDateToMMDDYYY,
    isValidDate,
  };
}
